import { memo, useId, useLayoutEffect, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { useDebugInteractivity } from './use-interactive'
import { interactiveRegistry } from './registry'

/**
 * Marks its parent box as interactive by registering itself with the shared
 * `interactiveRegistry`. It does no measurement of its own — a single loop
 * (see `use-bounds-sync`) reads all registered elements in one batch.
 */
export const InteractiveTracker = memo(() => {
  const id = useId()
  const ref = useRef<HTMLSpanElement>(null)
  const debugInteractivity = useDebugInteractivity()

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    interactiveRegistry.register(id, el)
    return () => interactiveRegistry.unregister(id)
  }, [id])

  return (
    <span
      aria-hidden
      ref={ref}
      className={twMerge(
        'pointer-events-none absolute inset-0 z-[2]',
        debugInteractivity ? 'border border-dashed' : 'bg-transparent',
      )}
    />
  )
})
InteractiveTracker.displayName = 'InteractiveTracker'
